import {
  HEADERS_REEMA_IMPERSONATION_USER_ID,
  HEADERS_REEMA_INTEGRATION_USER_EMAIL,
  HEADERS_REEMA_INTEGRATION_USER_ID,
} from '@act/shared/constants';
import { getIntegrationAuthConfig } from './integrationAuth.util';
import { SerializedError } from '@reduxjs/toolkit';
import { ValidationError } from 'class-validator';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';
import { ApiRequestDefaults, ApiRequestParams } from './apiRequestParams';
import { EndpointDefinition } from './endpointDefinition';
import { getToken } from './firebaseAuth.util';
import { dedupeRequest } from './requestDedupe.util';
import { buildRequestDedupeKey } from './requestDedupeKey.util';
import { readRequestIdentitySnapshot } from './requestIdentity.util';
import { stringifyQuery } from './stringifyQuery.util';

export interface ApiRequestError extends SerializedError {
  errors: ValidationError[];
  body: object;
  isAccessDenied?: boolean;
}

interface Pagination {
  pageSize: number;
  pageIndex: number;
}

type MakeRequestConfig<BODY extends object, PARAMS extends ApiRequestParams> = {
  params?: PARAMS;
  body?: BODY;
  defaults?: ApiRequestDefaults;
};

const shouldDedupeInFlight = (method: string): boolean =>
  method === 'GET' || method === 'HEAD';

/**
 * Performs an HTTP request for the given endpoint definition.
 * Concurrent identical GET/HEAD calls (same params/body/identity) share one
 * in-flight promise. Identity includes impersonation, integration auth, and language.
 * @param endpoint - Route, method, and defaults for the call
 * @param request - Optional params, body, and defaults override bag
 * @returns Parsed response body (or undefined for empty bodies)
 */
export const makeRequest = async <
  RESPONSE extends object | void,
  BODY extends object,
  PARAMS extends ApiRequestParams,
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>,
  request: MakeRequestConfig<BODY, PARAMS>
): Promise<RESPONSE> => {
  if (!shouldDedupeInFlight(endpoint.method)) {
    return executeMakeRequest(endpoint, request);
  }
  const cacheKey = buildRequestDedupeKey(
    endpoint.name,
    request.params,
    request.body,
    readRequestIdentitySnapshot()
  );
  return dedupeRequest(cacheKey, () => executeMakeRequest(endpoint, request));
};

const executeMakeRequest = async <
  RESPONSE extends object | void,
  BODY extends object,
  PARAMS extends ApiRequestParams,
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>,
  {
    params,
    body,
  }: MakeRequestConfig<BODY, PARAMS>
): Promise<RESPONSE> => {
  const url = buildUrl(endpoint.route, params, null, endpoint.defaults);
  const integrationAuth = getIntegrationAuthConfig();
  const token = integrationAuth ? null : await getToken();
  const impersonatedUserId = localStorage.getItem('impersonatedUserId');
  const language = localStorage.getItem('language');

  const response = await fetch(url, {
    method: endpoint.method,
    headers: {
      ...(integrationAuth && {
        [HEADERS_REEMA_INTEGRATION_USER_ID]: integrationAuth.userId,
        ...(integrationAuth.email && {
          [HEADERS_REEMA_INTEGRATION_USER_EMAIL]: integrationAuth.email,
        }),
      }),
      ...(impersonatedUserId && {
        [HEADERS_REEMA_IMPERSONATION_USER_ID]: impersonatedUserId,
      }),
      ...(language && {
        ['x-reema-language']: language,
      }),
      ...(!integrationAuth && {
        Authorization: token ? `Bearer ${token}` : '',
      }),
      'Content-Type': `application/json`,
    },
    body: body === null ? null : JSON.stringify(body),
  });

  if (!response.ok) {
    const finalizedResponse = await response.json();
    const formValidationError = finalizedResponse.errors;
    const isAccessDenied = response.headers.get('X-Access-Denied') === 'true';
    const serializedError: ApiRequestError = {
      name: 'HttpError',
      message: `${response.status} error for route ${response.url}`,
      code: response.status.toString(),
      body: finalizedResponse,
      errors: formValidationError,
      isAccessDenied,
    };
    throw serializedError;
  }

  const responseText = await response.text();

  if (responseText === '') {
    return undefined as RESPONSE;
  }

  const responseBody = JSON.parse(responseText) as RESPONSE;
  return responseBody;
};

/**
 * Encodes a path segment the same way legacy querystring encoding did.
 * @param str - Raw path segment.
 * @returns Encoded segment safe for a URL path.
 */
const httpEscape = (str: string): string => encodeURIComponent(str);

const buildUrl = (
  url: string,
  params: ApiRequestParams,
  pagination: Pagination,
  defaults?: ApiRequestDefaults
): string => {
  let queryParamKeys: string[] = [];
  let routeParamKeys: string[] = [];
  const combinedParams = { ...params };

  if (params || defaults?.params) {
    if (defaults?.params) {
      Object.entries(defaults.params).forEach(([key, value]) => {
        if (combinedParams[key] === undefined) {
          combinedParams[key] = value;
        }
      });
    }

    const routeParamMatch = url.match(/\/:(\w|\d|-)+/g);
    if (routeParamMatch) {
      routeParamKeys = routeParamMatch
        .map(m => m.replace('/:', ''))
        .filter(key => combinedParams[key] !== undefined);
    }
    queryParamKeys = Object.entries(combinedParams)
      .filter(([key, value]) => !routeParamKeys.find(k => k === key) && value)
      .map(([key]) => key);
  }

  if (routeParamKeys?.length) {
    url = routeParamKeys.reduce((acc: string, key): string => {
      const value = combinedParams[key];
      if (value != null) {
        return acc.replace(`:${key}`, httpEscape(value.toString()));
      }
      return acc;
    }, url);
  }

  if (queryParamKeys || pagination) {
    const query: ApiRequestParams = queryParamKeys.reduce(
      (queryParams, key) => {
        queryParams[key] = combinedParams[key];
        return queryParams;
      },
      {}
    );

    if (pagination) {
      query['page-start'] = pagination.pageIndex * pagination.pageSize;
      query['page-size'] = pagination.pageSize;
    }

    // Array to comma delimited string
    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query[key] = value.join(',');
      }
    });
    const qs = stringifyQuery(
      query as Record<string, string | number | boolean | null | undefined>
    );
    if (qs && qs != '') {
      url = `${url}?${qs}`;
    }
  }

  const baseUrl = getEnvironment().serviceUris.websocketService;
  return baseUrl + url;
};
